import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { cn } from '@/utils/cn';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import { parseDateValue } from '@/utils/dateInput';
import { intlMonthYearLabel } from '@/utils/intlCalendar';
import { CalendarWeekdayHeader } from '@/components/ui/CalendarWeekdayHeader';

export type MultiDayCalendarMode = 'single' | 'multiple';

export type MultiDayCalendarPickerProps = {
  label?: string;
  value?: string[];
  onChange?: (dates: string[]) => void;
  mode?: MultiDayCalendarMode;
  min?: string;
  max?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  className?: string;
  disabled?: boolean;
  /** yyyy-MM-dd keys that cannot be newly selected (already selected days can still be removed). */
  disabledDates?: string[];
};

type DragMode = 'add' | 'remove';

type DragState = {
  active: boolean;
  mode: DragMode;
  touched: Set<string>;
  working: string[];
};

function sortDates(dates: string[]): string[] {
  return [...new Set(dates.map((d) => d.slice(0, 10)).filter(Boolean))].sort();
}

function formatDateValue(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function applyDragDay(dates: string[], dayKey: string, mode: DragMode): string[] {
  if (mode === 'add') {
    return dates.includes(dayKey) ? dates : sortDates([...dates, dayKey]);
  }
  return dates.filter((d) => d !== dayKey);
}

export function hrSelectedDatesBounds(dates: string[]): { start: string; end: string } | null {
  const sorted = sortDates(dates);
  if (sorted.length === 0) return null;
  return { start: sorted[0], end: sorted[sorted.length - 1] };
}

export function hrFormatSelectedDatesSummary(dates: string[]): string {
  const sorted = sortDates(dates);
  if (sorted.length === 0) return 'No days selected';
  if (sorted.length === 1) return format(parseISO(sorted[0]), 'MMM d, yyyy');
  return `${sorted.length} separate days selected`;
}

export function MultiDayCalendarPicker({
  label = 'Select days',
  value = [],
  onChange,
  mode = 'multiple',
  min,
  max,
  required,
  hint,
  error,
  className,
  disabled = false,
  disabledDates = [],
}: MultiDayCalendarPickerProps) {
  const { t } = useTranslation('common');
  const { text: labelText, required: isRequired } = parseFieldLabel(label, required);
  const selected = useMemo(() => sortDates(value), [value]);
  const [viewMonth, setViewMonth] = useState(() => {
    const first = selected[0] ? parseDateValue(selected[0]) : null;
    return first ?? new Date();
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<DragState | null>(null);

  const minDate = parseDateValue(min);
  const maxDate = parseDateValue(max);
  const blockedDates = useMemo(() => new Set(disabledDates.map((d) => d.slice(0, 10))), [disabledDates]);

  const days = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const monthEnd = endOfMonth(viewMonth);
    return eachDayOfInterval({
      start: startOfWeek(monthStart),
      end: endOfWeek(monthEnd),
    });
  }, [viewMonth]);

  useEffect(() => {
    const first = selected[0] ? parseDateValue(selected[0]) : null;
    if (first) setViewMonth(first);
  }, [selected[0]]);

  useEffect(() => {
    const endDrag = () => {
      dragRef.current = null;
      setIsDragging(false);
    };
    window.addEventListener('mouseup', endDrag);
    return () => window.removeEventListener('mouseup', endDrag);
  }, []);

  const isDisabled = (day: Date) => {
    if (disabled) return true;
    if (minDate && day < minDate && !isSameDay(day, minDate)) return true;
    if (maxDate && day > maxDate && !isSameDay(day, maxDate)) return true;
    const key = formatDateValue(day);
    if (blockedDates.has(key) && !selected.includes(key)) return true;
    return false;
  };

  const commitSelection = (dates: string[]) => {
    onChange?.(sortDates(dates));
  };

  const removeDay = (dayKey: string) => {
    if (!onChange || disabled) return;
    commitSelection(selected.filter((d) => d !== dayKey));
  };

  const handleDayMouseDown = (day: Date) => {
    if (isDisabled(day) || !onChange) return;
    const key = formatDateValue(day);
    if (mode === 'single') {
      commitSelection([key]);
      return;
    }
    const dragMode: DragMode = selected.includes(key) ? 'remove' : 'add';
    const next = applyDragDay(selected, key, dragMode);
    commitSelection(next);
    dragRef.current = {
      active: true,
      mode: dragMode,
      touched: new Set([key]),
      working: next,
    };
    setIsDragging(true);
  };

  const handleDayMouseEnter = (day: Date) => {
    const drag = dragRef.current;
    if (!drag?.active || isDisabled(day) || !onChange || mode === 'single') return;
    const key = formatDateValue(day);
    if (drag.touched.has(key)) return;
    drag.touched.add(key);
    drag.working = applyDragDay(drag.working, key, drag.mode);
    commitSelection(drag.working);
  };

  return (
    <div className={cn('space-y-2', className)}>
      {labelText ? <FieldLabel label={labelText} required={isRequired} /> : null}
      {hint ? <p className="text-xs text-slate-500 dark:text-slate-400">{hint}</p> : null}
      {error ? <p className="text-xs text-red-600 dark:text-red-400">{error}</p> : null}
      <div
        className={cn(
          'rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-600 dark:bg-slate-900',
          disabled && 'opacity-60',
        )}
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <button
            type="button"
            className="calendar-nav-btn"
            onClick={() => setViewMonth(addMonths(viewMonth, -1))}
            disabled={disabled}
            aria-label={t('labels.previousMonth')}
          >
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </button>
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {intlMonthYearLabel(viewMonth)}
          </span>
          <button
            type="button"
            className="calendar-nav-btn"
            onClick={() => setViewMonth(addMonths(viewMonth, 1))}
            disabled={disabled}
            aria-label={t('labels.nextMonth')}
          >
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        <CalendarWeekdayHeader />

        <div
          className={cn('grid grid-cols-7 gap-0.5', isDragging && 'calendar-grid--selecting')}
          onMouseLeave={() => {
            dragRef.current = null;
            setIsDragging(false);
          }}
        >
          {days.map((day) => {
            const inMonth = isSameMonth(day, viewMonth);
            const key = formatDateValue(day);
            const isSelected = selected.includes(key);
            const dayDisabled = isDisabled(day);
            return (
              <button
                key={key}
                type="button"
                disabled={dayDisabled}
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleDayMouseDown(day);
                }}
                onMouseEnter={() => handleDayMouseEnter(day)}
                className={cn(
                  'calendar-day',
                  !inMonth && 'calendar-day--muted',
                  isSelected && 'calendar-day--selected',
                  isToday(day) && !isSelected && 'calendar-day--today',
                  dayDisabled && 'calendar-day--disabled',
                  isDragging && !dayDisabled && 'calendar-day--dragging',
                )}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-600 dark:text-slate-400">
            {mode === 'single'
              ? selected.length > 0
                ? `Selected: ${hrFormatSelectedDatesSummary(selected)}`
                : 'Tap one day'
              : hrFormatSelectedDatesSummary(selected)}
          </p>
          {mode === 'multiple' && selected.length > 0 && !disabled ? (
            <button
              type="button"
              className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              onClick={() => commitSelection([])}
            >
              Clear all
            </button>
          ) : null}
        </div>

        {selected.length > 0 && mode === 'multiple' ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {selected.map((day) => (
              <span
                key={day}
                className="inline-flex items-center gap-1 rounded-full bg-primary-50 py-0.5 pl-2 pr-1 text-[11px] font-medium text-primary-800 dark:bg-primary-950/50 dark:text-primary-200"
              >
                {format(parseISO(day), 'MMM d')}
                {!disabled ? (
                  <button
                    type="button"
                    className="rounded-full p-0.5 text-primary-700 hover:bg-primary-100 dark:text-primary-300 dark:hover:bg-primary-900/60"
                    aria-label={`Remove ${day}`}
                    onClick={() => removeDay(day)}
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </button>
                ) : null}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
